import { useCallback, useRef, useState } from 'react';
import { ImportData, ImportLineItem, ImportMetadata, LineClassification } from '../types';
import { useUndoRedo } from './useUndoRedo';
import { useLineActions } from './useLineActions';
import { usePdfNavigation } from './usePdfNavigation';
import { useHighlightSync } from './useHighlightSync';

export function useImportState(data: ImportData) {
  const [metadata, setMetadata] = useState<ImportMetadata>(data.metadata);
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSelectedRef = useRef<string | null>(null);

  const {
    present: lines,
    set: setLines,
    update: updateLines,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo<ImportLineItem[]>(data.lines);

  const mergeErrorTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleMergeError = useCallback((msg: string) => {
    clearTimeout(mergeErrorTimer.current);
    setMergeError(msg);
    mergeErrorTimer.current = setTimeout(() => setMergeError(null), 8000);
  }, []);

  const lineActions = useLineActions(lines, updateLines, handleMergeError);

  // Wrap updateLine to also track modifications
  const updateLine = useCallback(
    (id: string, field: keyof ImportLineItem, value: string | number | null) => {
      lineActions.updateLine(id, field, value);
      setModifiedFields((prev) => {
        const next = new Set(prev);
        next.add(`${id}:${field}`);
        return next;
      });
    },
    [lineActions]
  );

  const pdfNav = usePdfNavigation(data.totalPages);

  const highlightSync = useHighlightSync();

  const updateMetadata = useCallback(
    (field: keyof ImportMetadata, value: string) => {
      setMetadata((prev) => ({ ...prev, [field]: value }));
      setModifiedFields((prev) => {
        const next = new Set(prev);
        next.add(`meta:${field}`);
        return next;
      });
    },
    []
  );

  const dismissMergeError = useCallback(() => setMergeError(null), []);

  const toggleSelect = useCallback(
    (id: string, shiftKey: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (shiftKey && lastSelectedRef.current) {
          // Range select
          const ids = lines.map((l) => l.id);
          const from = ids.indexOf(lastSelectedRef.current);
          const to = ids.indexOf(id);
          if (from >= 0 && to >= 0) {
            const [start, end] = from < to ? [from, to] : [to, from];
            for (let i = start; i <= end; i++) {
              next.add(ids[i]);
            }
          }
        } else {
          if (next.has(id)) next.delete(id);
          else next.add(id);
        }
        lastSelectedRef.current = id;
        return next;
      });
    },
    [lines]
  );

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === lines.length) return new Set();
      return new Set(lines.map((l) => l.id));
    });
  }, [lines]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
  }, []);

  const updateClassification = useCallback(
    (id: string, classification: LineClassification) => {
      // User touched a dropdown → changed (or unclassified if all cleared)
      const status: LineClassification['status'] =
        !classification.lotId && !classification.ensembleId && !classification.sousEnsembleId
          ? 'unclassified'
          : 'changed';
      updateLines((prev) =>
        prev.map((l) =>
          l.id === id ? { ...l, classification: { ...classification, status } } : l
        )
      );
    },
    [updateLines]
  );

  const batchClassify = useCallback(
    (ids: string[], partial: Partial<LineClassification>) => {
      updateLines((prev) =>
        prev.map((l) => {
          if (!ids.includes(l.id)) return l;
          return {
            ...l,
            classification: {
              lotId: null,
              ensembleId: null,
              sousEnsembleId: null,
              ...l.classification,
              ...partial,
              status: 'changed' as const,
            },
          };
        })
      );
    },
    [updateLines]
  );

  const clearClassification = useCallback(
    (ids: string[]) => {
      updateLines((prev) =>
        prev.map((l) => {
          if (!ids.includes(l.id)) return l;
          const { classification: _, ...rest } = l;
          return rest;
        })
      );
    },
    [updateLines]
  );

  // "Valider l'import" → all classified lines become validated
  const validateImport = useCallback(() => {
    updateLines((prev) =>
      prev.map((l) => {
        if (!l.classification?.lotId) return l;
        return {
          ...l,
          classification: { ...l.classification, status: 'validated' as const },
        };
      })
    );
  }, [updateLines]);

  return {
    metadata,
    updateMetadata,
    lines,
    setLines,
    undo,
    redo,
    canUndo,
    canRedo,
    addLine: lineActions.addLine,
    deleteLine: lineActions.deleteLine,
    updateLine,
    mergeLine: lineActions.mergeLine,
    splitLine: lineActions.splitLine,
    reorderLines: lineActions.reorderLines,
    modifiedFields,
    mergeError,
    dismissMergeError,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    updateClassification,
    batchClassify,
    clearClassification,
    validateImport,
    pdfNav,
    ...highlightSync,
    pdfUrl: data.pdfUrl,
    totalPages: data.totalPages,
  };
}
